import useSwr from 'swr'
import { API_URL } from '@/utils/constants'
import Wallet from '@/utils/wallet'
import { WalletData } from '@/utils/types/wallet'
import axios from 'axios'

// Fetches balance for a specific address. Treats the dual-address case
// (wallets that split payments + taproot like Xverse) by merging both sources
// when the viewer is looking at their OWN wallet. For any foreign address we
// just POST to that address directly — no merging, no substitutions.
const fetcher = async ([_key, address, paymentsAddr, taprootAddr]: [
	string,
	string | undefined,
	string | undefined,
	string | undefined
]) => {
	const isDual =
		!!paymentsAddr && !!taprootAddr && paymentsAddr !== taprootAddr

	// Case A: no address supplied → viewing own wallet (logged in)
	if (!address) {
		if (isDual) {
			const [{ data: balance }, { data: inscriptions }] = await Promise.all([
				axios.post(`${API_URL}/wallet/${paymentsAddr}`),
				axios.post(`${API_URL}/wallet/${taprootAddr}`)
			])
			return {
				balance: balance?.balance,
				confirmed_balance: balance?.confirmed_balance,
				unconfirmed_balance: balance?.unconfirmed_balance,
				frozen_balance: balance?.frozen_balance,
				utxo_count: balance?.utxo_count,
				inscriptions: inscriptions?.inscriptions,
				brc20: inscriptions?.brc20,
				inscription_balance: inscriptions?.inscription_balance
			} as WalletData
		}

		const target = paymentsAddr || taprootAddr
		if (!target) return undefined
		const { data } = await axios.post(`${API_URL}/wallet/${target}`)
		return data as WalletData
	}

	// Case B: explicit address supplied, and it matches one side of the user's
	// own dual-address wallet → merge so the "own wallet" view still works
	// correctly when someone shares their own public URL with themselves.
	if (isDual && (address === paymentsAddr || address === taprootAddr)) {
		const [{ data: balance }, { data: inscriptions }] = await Promise.all([
			axios.post(`${API_URL}/wallet/${paymentsAddr}`),
			axios.post(`${API_URL}/wallet/${taprootAddr}`)
		])
		return {
			balance: balance?.balance,
			confirmed_balance: balance?.confirmed_balance,
			unconfirmed_balance: balance?.unconfirmed_balance,
			frozen_balance: balance?.frozen_balance,
			utxo_count: balance?.utxo_count,
			inscriptions: inscriptions?.inscriptions,
			brc20: inscriptions?.brc20,
			inscription_balance: inscriptions?.inscription_balance
		} as WalletData
	}

	// Case C: foreign address — just fetch that address directly.
	const { data } = await axios.post(`${API_URL}/wallet/${address}`)
	return data as WalletData
}

export default function useWalletBalance(address?: string): {
	data: WalletData
	isLoading: boolean
	mutate: any
} {
	// Resolve user's own addresses (if connected) so the fetcher can merge
	// when appropriate. Guard for SSR where `Wallet` may not be populated.
	let paymentsAddr: string | undefined
	let taprootAddr: string | undefined
	try {
		paymentsAddr = Wallet.balanceAddress?.()
		taprootAddr = Wallet.getTaprootAddress?.()
	} catch {
		/* not connected */
	}

	const targetForKey = address || paymentsAddr || taprootAddr || 'anon'
	return useSwr(
		[`${API_URL}/wallet/${targetForKey}`, address, paymentsAddr, taprootAddr],
		fetcher
	) as any
}
